import { useAiAssistantStore } from '~/stores/aiAssistantStore';
import { AI_ROLE, AI_ASSISTANT_STATUS, AI_MESSAGE_TYPE } from '~/stores/aiAssistantTypes';
import { useAiChat } from '~/composables/useAiChat';
import { AI_ENDPOINTS, useAiEndpoints } from '~/composables/useAiEndpoints';
import { useEvent, useListen, useIgnore } from '~/composables/useEventBus';
import EndpointFactory from '~/app/factories/EndpointFactory';
import { ModelType } from '~/app/base/schemas/BaseSchema';
import ModelFileSchema from '~/app/common/schemas/ModelFileSchema';
import { useAuthStore } from '~/stores/authStore';

export const useAiAssistant = () => {
    const store = useAiAssistantStore();
    const { subscribe, unsubscribe, sendMessage: sendChatMessage, abort } = useAiChat();
    const { t } = useTranslation();
    const { url, getHeaders } = useAiEndpoints();
    const authStore = useAuthStore();

    const getChannelName = (): string => {
        const tenantId = authStore.tenant?.getId() ?? '';
        const userId = authStore.authed?.getId() ?? '';
        return `tenant.${tenantId}.ai-chat.${userId}`;
    };

    const subscribeToChannel = () => {
        subscribe(getChannelName());
    };

    const unsubscribeFromChannel = () => {
        unsubscribe();
    };

    const loadConversation = async () => {
        if (!store.documentId) return;

        store.setStatus(AI_ASSISTANT_STATUS.PROCESSING);

        try {
            const params = new URLSearchParams({
                conversable_type: store.documentType ?? 'invoice',
                conversable_id: store.documentId,
            });

            const res = await fetch(`${url(AI_ENDPOINTS.conversations)}?${params}`, {
                headers: getHeaders(),
            });

            if (!res.ok) {
                store.setStatus(AI_ASSISTANT_STATUS.IDLE);
                return;
            }

            const data = await res.json();
            if (!data.conversation_id) {
                store.setStatus(AI_ASSISTANT_STATUS.IDLE);
                return;
            }

            store.conversationId = data.conversation_id;

            const messagesRes = await fetch(url(AI_ENDPOINTS.conversationMessages(data.conversation_id)), {
                headers: getHeaders(),
            });

            if (messagesRes.ok) {
                const messagesData = await messagesRes.json();
                store.loadHistory(messagesData.data ?? []);
            }
        } catch {
            // Silently fail — just show empty chat
        }

        store.setStatus(AI_ASSISTANT_STATUS.IDLE);
    };

    const sendMessage = async (text: string, metadata?: Record<string, any>, displayText?: string) => {
        store.dismissUnansweredChoices();
        store.pendingQuestions = [];
        store.batchedSelections = [];

        // Skip the user bubble for interactive selections — the radio widget itself
        // shows the picked option (selected state + yellow ring), so an extra bubble
        // duplicates the same information. Matches what the formatter does on reload.
        const isInteractiveSelection = !!metadata?.interactive_response;
        if (!isInteractiveSelection) {
            store.addMessage(AI_ROLE.USER, displayText ?? text);
        }

        store.setStatus(AI_ASSISTANT_STATUS.PROCESSING);

        try {
            const response = await sendChatMessage({
                message: text,
                conversationId: store.conversationId,
                conversableType: store.documentType,
                conversableId: store.documentId,
                metadata: metadata ?? null,
            });

            if (response) {
                if (store.createdDocumentId && store.createdDocumentType) {
                    useEvent('ai:document-created', {
                        id: store.createdDocumentId,
                        type: store.createdDocumentType,
                    });
                }
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : '';
            store.setError(message || t('ai.chat.error'));
            store.addMessage(AI_ROLE.ASSISTANT, t('ai.chat.generic_error'));
            store.setStatus(AI_ASSISTANT_STATUS.IDLE);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} o`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    };

    const uploadFile = async (file: File, documentId?: string) => {
        const resolvedId = documentId ?? store.documentId;
        if (!resolvedId) return;

        const thumbnail = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;

        store.addMessage(AI_ROLE.USER, `${file.name} — ${formatFileSize(file.size)}`, {
            type: AI_MESSAGE_TYPE.FILE,
            fileInfo: { name: file.name, size: file.size, thumbnail },
        });

        try {
            const endpoint = EndpointFactory.make(ModelType.MODEL_FILES);
            const schema = ModelFileSchema.make({ attributes: { title: file.name } });
            schema.addToOneRelationship('model', ModelType.INVOICES, resolvedId);

            await endpoint.upload(schema, file);
            store.addMessage(AI_ROLE.ASSISTANT, t('ai.chat.file_added'));
        } catch {
            store.addMessage(AI_ROLE.ASSISTANT, t('ai.chat.file_upload_error'));
        }
    };

    return {
        sendMessage,
        loadConversation,
        uploadFile,
        abort,
        subscribeToChannel,
        unsubscribeFromChannel,
    };
};
